import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { CalendarPlus, Loader2 } from "lucide-react";

const DAYS_OPTIONS = [3, 5, 7, 10, 14, 21, 30];

/**
 * FollowupSetter — doctor sets a follow-up reminder for the current patient.
 * Renders a Dialog. Save fires POST /patients/{id}/followup which will
 * (a) create/replace a pending row in db.followups and
 * (b) be auto-notified by the daily 9AM APScheduler job.
 *
 * Props:
 *   api: authenticated axios instance
 *   patient: {id, name, ...}
 *   trigger: optional custom trigger element
 *   onSaved: callback(followupRow) after save
 */
export default function FollowupSetter({ api, patient, trigger, onSaved }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState("7");
  const [note, setNote] = useState("");
  const [lang, setLang] = useState("en");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/followup`, {
        followup_days: parseInt(days),
        followup_note: note.trim() || null,
        lang,
      });
      toast.success(`Follow-up scheduled for ${data.followup_date}`);
      setOpen(false);
      setNote("");
      if (onSaved) onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="gap-2 h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`open-followup-${patient?.id}`}>
            <CalendarPlus className="w-4 h-4" /> Set Follow-up
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="followup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-700">
            <CalendarPlus className="w-5 h-5" /> Follow-up for {patient?.name}
          </DialogTitle>
          <DialogDescription>
            UpchaarQ will automatically WhatsApp + SMS this patient one day before the follow-up date.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Follow-up in</Label>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger data-testid="followup-days-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DAYS_OPTIONS.map((d) => (
                  <SelectItem key={d} value={String(d)}>{d} days</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Reason (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={100}
              placeholder="e.g. Review test results, dressing change…"
              data-testid="followup-note-input"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Patient's preferred language</Label>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger data-testid="followup-lang-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="hi">Hindi (Roman)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
            data-testid="followup-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
            {busy ? "Setting…" : "Set Reminder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
